import { AuthService } from '../services/auth';

export class AuthComponent {
    private element: HTMLElement;
    private auth: AuthService;

    constructor(element: HTMLElement) {
        this.element = element;
        this.auth = AuthService.getInstance();
    }

    async init(): Promise<void> {
        await this.auth.initialize();

        if (this.auth.isAuthenticated()) {
            this.clearAuthComponents();
            return;
        }

        this.renderAuthGuard();
    }

    private renderAuthGuard(): void {
        this.element.innerHTML = `
            <div class="auth-guard">
                <div class="auth-box">
                    <h1>🤖 Chat</h1>
                    <p>Sign in to continue</p>
                    <button id="auth-sign-in" class="auth-button">Sign In</button>
                    <button id="auth-sign-up" class="auth-button secondary">Sign Up</button>
                </div>
            </div>
        `;

        this.bindAuthEvents();
        (window as any).lucide?.createIcons();
    }

    private bindAuthEvents(): void {
        this.element.querySelector('#auth-sign-in')?.addEventListener('click', () => {
            window.location.hash = '#/sign-in';
        });

        this.element.querySelector('#auth-sign-up')?.addEventListener('click', () => {
            window.location.hash = '#/sign-up';
        });
    }

    private clearAuthComponents(): void {
        const authGuard = this.element.querySelector('.auth-guard');
        if (authGuard) authGuard.remove();
    }
}

export class SignInComponent {
    private element: HTMLElement;
    private auth: AuthService;
    private clerkElement: HTMLElement | null = null;
    private unsubscribe: (() => void) | null = null;

    constructor(element: HTMLElement) {
        this.element = element;
        this.auth = AuthService.getInstance();
    }

    render(): void {
        console.log('[SignIn] Rendering');
        this.element.innerHTML = `
            <div class="auth-page">
                <div class="auth-card">
                    <button type="button" id="back-button" class="auth-link" style="margin-bottom:;">← Back</button>
                    <h1>Sign In</h1>
                    <div id="clerk-mount-sign-in"></div>
                </div>
            </div>
        `;
        console.log('[SignIn] HTML rendered, mounting Clerk component');
        this.bindEvents();
        this.mountClerk();
    }

    private mountClerk(): void {
        const clerk = this.auth.getClerk();
        if (!clerk) {
            console.error('[SignIn] Clerk not initialized');
            return;
        }

        this.clerkElement = this.element.querySelector('#clerk-mount-sign-in');
        if (!this.clerkElement) {
            console.error('[SignIn] Mount element not found');
            return;
        }

        clerk.mountSignIn(this.clerkElement, {
            appearance: {
                elements: {
                    rootBox: { display: 'flex', flexDirection: 'column', gap: '16px' }
                }
            }
        });

        this.unsubscribe = clerk.addListener((event) => {
            if (event.user && event.session) {
                window.location.hash = '#/';
            }
        });
    }

    private bindEvents(): void {
        this.element.querySelector('#back-button')?.addEventListener('click', () => {
            window.location.hash = '#/';
        });
    }

    destroy(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }

        const clerk = this.auth.getClerk();
        if (clerk && this.clerkElement) {
            clerk.unmountSignIn(this.clerkElement);
        }

        const authPage = this.element.querySelector('.auth-page');
        if (authPage) {
            authPage.remove();
        }
    }
}

export class SignUpComponent {
    private element: HTMLElement;
    private auth: AuthService;
    private clerkElement: HTMLElement | null = null;
    private unsubscribe: (() => void) | null = null;

    constructor(element: HTMLElement) {
        this.element = element;
        this.auth = AuthService.getInstance();
    }

    render(): void {
        console.log('[SignUp] Rendering');
        this.element.innerHTML = `
            <div class="auth-page">
                <div class="auth-card">
                    <button type="button" id="back-button" class="auth-link" style="margin-bottom: 1rem;">← Back</button>
                    <h1>Sign Up</h1>
                    <div id="clerk-mount-sign-up"></div>
                </div>
            </div>
        `;
        console.log('[SignUp] HTML rendered, mounting Clerk component');
        this.bindEvents();
        this.mountClerk();
    }

    private mountClerk(): void {
        const clerk = this.auth.getClerk();
        if (!clerk) {
            console.error('[SignUp] Clerk not initialized');
            return;
        }

        this.clerkElement = this.element.querySelector('#clerk-mount-sign-up');
        if (!this.clerkElement) {
            console.error('[SignUp] Mount element not found');
            return;
        }

        clerk.mountSignUp(this.clerkElement, {
            appearance: {
                elements: {
                    rootBox: { display: 'flex', flexDirection: 'column', gap: '16px' }
                }
            }
        });

        this.unsubscribe = clerk.addListener((event) => {
            if (event.user && event.session) {
                window.location.hash = '#/';
            }
        });
    }

    private bindEvents(): void {
        this.element.querySelector('#back-button')?.addEventListener('click', () => {
            window.location.hash = '#/';
        });
    }

    destroy(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }

        const clerk = this.auth.getClerk();
        if (clerk && this.clerkElement) {
            clerk.unmountSignUp(this.clerkElement);
        }

        const authPage = this.element.querySelector('.auth-page');
        if (authPage) {
            authPage.remove();
        }
    }
}

export class UserMenuComponent {
    private element: HTMLElement;
    private auth: AuthService;

    constructor(element: HTMLElement) {
        this.element = element;
        this.auth = AuthService.getInstance();
    }

    render(): void {
        const user = this.auth.getUser();
        if (!user) return;

        const email = user.emailAddresses[0]?.emailAddress;
        const initials = this.getInitials(email || 'User');
        const isSidebar = this.element.closest('.sidebar-footer') !== null;

        this.element.innerHTML = `
            <div class="user-menu">
                <button id="user-menu-button" class="user-menu-button">
                    <div class="user-avatar">${initials}</div>
                    ${!isSidebar ? `<span class="user-email">${email}</span>` : ''}
                    <i data-lucide="chevron-down"></i>
                </button>
                <div id="user-dropdown" class="user-dropdown hidden">
                    <div class="user-info">
                        <div class="user-avatar-large">${initials}</div>
                        <div class="user-email-large">${email}</div>
                    </div>
                    <div class="user-divider"></div>
                    <button id="settings-button" class="user-menu-item">
                        <i data-lucide="settings"></i>
                        <span>Settings</span>
                    </button>
                    <button id="sign-out-button" class="user-menu-item">
                        <i data-lucide="log-out"></i>
                        <span>Sign Out</span>
                    </button>
                </div>
            </div>
        `;

        (window as any).lucide?.createIcons();
        this.bindEvents();
    }

    private bindEvents(): void {
        const menuButton = this.element.querySelector('#user-menu-button') as HTMLButtonElement;
        const dropdown = this.element.querySelector('#user-dropdown') as HTMLDivElement;

        menuButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.classList.toggle('hidden');
        });

        document.addEventListener('click', () => {
            dropdown.classList.add('hidden');
        });

        this.element.querySelector('#settings-button')?.addEventListener('click', () => {
            this.element.dispatchEvent(new CustomEvent('open-settings', { bubbles: true }));
        });

        this.element.querySelector('#sign-out-button')?.addEventListener('click', async () => {
            await this.auth.signOut();
        });
    }

    private getInitials(email: string): string {
        const parts = email.split('@')[0].split('.');
        if (parts.length > 1) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return email.substring(0, 2).toUpperCase();
    }

    destroy(): void {
        this.element.innerHTML = '';
    }
}